import React from 'react'
import { CheckTransferValues } from '../../containers/CheckTransfers/Types'
import { Text, Stack } from '@chakra-ui/react'
import TransferPreviewModal from '../TransferPreviewModal'
import TransferDetailsSection from '../TransferPreviewModal/TransferDetailsSection'
import CheckRecipientSection from './CheckRecipientSection'
import { deliveryMethodText, RecipientTypeText } from '../../containers/CheckTransfers/Types'
import { SearchAccountsResponsePayload } from '@Types'
import IraDistributionSection from './IraDistributionSection'
import { DistributionReasonInfo } from '@Types'
import { useFormikContext } from 'formik'
import useGetRecentActivity from '../../api/useGetRecentActivity'
import { useCreateCheckWithdrawal } from '../../api/useCreateCheckWithdrawal'
import { delayFn, getAllAccountNames } from '../../utils'
import { formatCheckWithdrawal } from './formatCheckWithdrawal'
import { CountryItem } from '../../reducers/types'
import { useCreateScheduleTransfer } from '../../api/useCreateScheduledTransfer'
import ModalTransferScheduledPreview from '../ModalTransferScheduledPreview/ModalTransferScheduledPreview'
import ModalTransferFeesPreview from '../ModalComponents/ModalTransferFeesPreview'
import useGetSchedules from '../../api/useGetSchedules'
import TransferAmountWarning from '../TransferAmountWarning'

export interface CheckTransferPreviewModalProps {
  isOpen: boolean
  onClose: () => void
  account: SearchAccountsResponsePayload
  fromAccount: string
  selectedReason?: DistributionReasonInfo | null
  setSelectedReason: (reason: DistributionReasonInfo | null) => void
  hasSubmitPermission: boolean
  countryList: CountryItem[]
  amountAvailable: number
}

const CheckTransferPreviewModal = ({
  isOpen,
  onClose,
  account,
  fromAccount,
  selectedReason,
  setSelectedReason,
  hasSubmitPermission,
  countryList,
  amountAvailable
}: CheckTransferPreviewModalProps) => {
  const formik = useFormikContext<CheckTransferValues>()
  const {
    transferAmountRaw,
    totalBalanceCheckbox,
    deliveryMethod,
    sendTo,
    beneficiaryName,
    recipientAddress,
    recipientAddressLine2,
    recipientCity,
    recipientState,
    recipientZip,
    recipientCountry,
    memo1,
    memo2,
    memo3,
    distributionReason,
    federalTaxWithholdingType,
    federalTaxWithholdingAmountRaw,
    federalTaxWithholdingAmountDisplay,
    stateTaxWithholdingType,
    stateTaxWithholdingAmountRaw,
    stateTaxWithholdingAmountDisplay,
    receivingInstitutionName,
    transferRecurrence,
    whenToSend,
    transferScheduledDate,
    transferStartDate,
    transferFrequency,
    numberOfTransfers,
    fees
  } = formik?.values

  const resetForm = () => {
    formik.resetForm()
    formik.validateForm()
    setSelectedReason(null)
  }
  const { refetch: RecentActivity } = useGetRecentActivity(account.accountNumber)
  const { refetch: refetchSchedulesData } = useGetSchedules(account.accountNumber)
  const { mutate: createCheckWithdrawal, isLoading: isSubmittingTransfer } = useCreateCheckWithdrawal({
    onSuccess: () => {
      onClose()
      delayFn(RecentActivity)
      resetForm()
    },
    onFail: () => {
      onClose()
      refetchSchedulesData()
    },
    amountAvailable
  })
  const { mutate: createScheduleTransfer, isLoading: isSubmittingSchedule } = useCreateScheduleTransfer(
    'check',
    'withdrawals',
    {
      onSuccess: () => {
        onClose()
        delayFn(RecentActivity)
        resetForm()
      },
      onSettled: () => {
        onClose()
        refetchSchedulesData()
      }
    }
  )

  const onSubmit = () => {
    const isScheduledTransfer =
      formik.values.transferRecurrence === 'RECURRING' || formik.values.whenToSend === 'IN_THE_FUTURE'
    if (isScheduledTransfer) {
      return createScheduleTransfer(
        formatCheckWithdrawal(account.accountNumber, formik.values, countryList, isScheduledTransfer)
      )
    } else {
      return createCheckWithdrawal(
        formatCheckWithdrawal(account.accountNumber, formik.values, countryList, isScheduledTransfer)
      )
    }
  }
  const allAccountNames = getAllAccountNames(account.accountTitle, account.accountNames)
  const getAddress = () => {
    if (sendTo === 'ACCOUNT-HOLDER') {
      const address = account.accountAddress
      return {
        address: address?.streetAddress[0],
        addressLine2: address?.streetAddress[1],
        city: address?.city,
        stateProvince: address?.state,
        postalCode: address?.postalCode,
        country: address?.country
      }
    }
    return {
      address: recipientAddress,
      addressLine2: recipientAddressLine2,
      city: recipientCity,
      stateProvince: recipientState,
      postalCode: recipientZip,
      country: recipientCountry
    }
  }

  return (
    <TransferPreviewModal
      isOpen={isOpen}
      onClose={onClose}
      header='Check Withdrawal'
      onSubmit={onSubmit}
      isSubmitting={isSubmittingTransfer || isSubmittingSchedule}
      isSubmitDisabled={!hasSubmitPermission}
      confirmLabel={distributionReason ? 'Confirm Distribution' : 'Confirm'}
    >
      <Stack pb={8} spacing={8}>
        <Text>Please review the details of your transfer below.</Text>
        <TransferDetailsSection
          fromAccount={fromAccount}
          transferAmount={transferAmountRaw}
          isTotalAmount={totalBalanceCheckbox}
          transferType='Check'
        />
        <CheckRecipientSection
          deliveryMethod={deliveryMethod ? deliveryMethodText[deliveryMethod] : ''}
          sendTo={sendTo ? RecipientTypeText[sendTo] : ''}
          recipientName={sendTo === 'THIRD-PARTY' ? beneficiaryName : allAccountNames || ''}
          recipientAddress={getAddress()}
          memo1={memo1}
          memo2={memo2}
          memo3={memo3}
        />
        <ModalTransferScheduledPreview
          transferRecurrence={transferRecurrence}
          whenToSend={whenToSend}
          transferScheduledDate={transferScheduledDate}
          transferStartDate={transferStartDate}
          transferFrequency={transferFrequency}
          numberOfTransfers={numberOfTransfers}
        />
        {!!selectedReason && (
          <IraDistributionSection
            selectedReason={selectedReason}
            receivingInstitutionName={receivingInstitutionName}
            federalTaxWithholdingType={federalTaxWithholdingType}
            federalTaxWithholdingAmountRaw={federalTaxWithholdingAmountRaw}
            federalTaxWithholdingAmountDisplay={federalTaxWithholdingAmountDisplay}
            stateTaxWithholdingType={stateTaxWithholdingType}
            stateTaxWithholdingAmountRaw={stateTaxWithholdingAmountRaw}
            stateTaxWithholdingAmountDisplay={stateTaxWithholdingAmountDisplay}
          />
        )}
        <ModalTransferFeesPreview fees={fees} />
        <TransferAmountWarning amountAvailable={amountAvailable} transferAmount={transferAmountRaw} />
      </Stack>
    </TransferPreviewModal>
  )
}

export default CheckTransferPreviewModal
